import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /**
   * Option values (human-readable names like 'Yellow', 'Excellent') saved
   * just before a sibling-product navigation, so they can be restored after
   * the new product's HTML is morphed in.
   * @type {(string | null)[]}
   */
  #savedOptionValues = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // Before navigating to a sibling product (different storage), save the
    // human-readable option values (e.g. 'Yellow', 'Excellent') from all
    // variant fieldsets. After the new product morphs in, #resetPillState()
    // will try to restore these selections on the new product.
    if (loadsNewProduct) {
      this.#savedOptionValues = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []).map(
        (fieldset) => fieldset.querySelector('input:checked')?.value ?? null
      );

      // Visually dim the product section during navigation so the user never
      // sees the wrong-color flash. Removed after updateMain() completes.
      this.#setSiblingNavLoading(true);
    }

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then(async (responseText) => {
        this.#pendingRequestUrl = undefined;
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          // ── Two-fetch approach for sibling navigation ─────────────────────
          // The first fetch returns the new product's DEFAULT variant (e.g.
          // 256GB + Black + Pristine 100%). Before rendering it, we look up
          // the new product's real option-value IDs for the saved names
          // (e.g. 'Yellow', 'Excellent') and do a second fetch so the server
          // returns the CORRECT variant HTML. Only then we call updateMain()
          // — the user never sees the wrong color or image.
          if (this.#savedOptionValues.some((v) => v !== null)) {
            const corrected = await this.#fetchCorrectedSiblingHtml(html, requestUrl);
            if (corrected) html = corrected;
          }

          this.updateMain(html);
          this.#setSiblingNavLoading(false);
          this.#resetPillState();
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
          this.#setSiblingNavLoading(false);
          this.#resetPillState();
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          this.#setSiblingNavLoading(false); // Always clear on error
          console.error(error);
        }
      });
  }

  /**
   * Given the default HTML for a new sibling product, finds the option value
   * IDs that match the saved option names (e.g. 'Yellow', 'Excellent'), then
   * fetches the product page again with those IDs so the server returns the
   * correct pre-selected variant HTML.
   *
   * Returns the corrected Document if successful, or null if no matching IDs
   * were found (caller will fall back to the default HTML).
   *
   * @param {Document} defaultHtml - The parsed default-variant HTML.
   * @param {string} originalUrl - The original request URL (used to build the corrected URL).
   * @returns {Promise<Document | null>}
   */
  async #fetchCorrectedSiblingHtml(defaultHtml, originalUrl) {
    const picker = defaultHtml.querySelector('variant-picker');
    if (!picker) return null;

    const fieldsets = Array.from(picker.querySelectorAll('fieldset[data-fieldset-index]'));
    /** @type {string[]} */
    const matchedIds = [];

    fieldsets.forEach((fieldset, i) => {
      const savedValue = this.#savedOptionValues[i];
      if (!savedValue) return;

      const inputs = Array.from(fieldset.querySelectorAll('input'));
      const match = inputs.find(
        (inp) =>
          inp instanceof HTMLInputElement &&
          inp.value === savedValue &&
          inp.dataset.optionAvailable !== 'false'
      );

      const id = match instanceof HTMLInputElement ? match.dataset.optionValueId : undefined;
      if (id) matchedIds.push(id);
    });

    // Nothing matched — fall back to default
    if (matchedIds.length === 0) {
      this.#savedOptionValues = [];
      return null;
    }

    // Build a corrected URL: strip old option_values, inject new ones
    const [base, query] = originalUrl.split('?');
    const params = new URLSearchParams(query ?? '');
    params.set('option_values', matchedIds.join(','));

    try {
      const response = await fetch(`${base}?${params}`, { signal: this.#abortController?.signal });
      const text = await response.text();
      const correctedHtml = new DOMParser().parseFromString(text, 'text/html');
      correctedHtml.querySelector('overflow-list[defer]')?.removeAttribute('defer');
      this.#savedOptionValues = []; // Consumed — no restoration needed in #resetPillState
      return correctedHtml;
    } catch {
      this.#savedOptionValues = [];
      return null;
    }
  }

  /**
   * Dims (or un-dims) the product section during sibling navigation to
   * prevent any visual flash while fetches are in flight.
   * @param {boolean} loading
   */
  #setSiblingNavLoading(loading) {
    // Target the closest product section or fall back to <main>.
    const target =
      this.closest('product-information, featured-product-information, [data-section-type="product"]') ??
      document.querySelector('main');
    if (!target || !(target instanceof HTMLElement)) return;
    if (loading) {
      target.dataset.siblingNavLoading = 'true';
    } else {
      delete target.dataset.siblingNavLoading;
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Resets pill animation state after a sibling-product morph.
   *
   * Root causes this solves:
   *
   *  A) morph.js explicitly preserves `data-previous-checked` / `data-current-checked`
   *     from old DOM nodes onto new nodes. After a sibling navigation the old
   *     product's checked positions (e.g. Yellow=index 2, Excellent=index 2)
   *     get stamped onto the new product's nodes, making the pill animate
   *     from those stale positions → the jarring sliding black box.
   *
   *  B) `data-current-checked` can end up on a node whose `:checked` attribute
   *     was removed by the morph, causing Color/Condition to appear unselected
   *     in the UI even though the price is correct.
   *
   * Note: the #savedOptionValues restoration and second-fetch that used to
   * happen here have been moved to #fetchCorrectedSiblingHtml(), which runs
   * BEFORE updateMain() so the user never sees the wrong state.
   *
   * Strategy:
   *   1. Suppress CSS transitions for one paint cycle (no-animate class).
   *   2. Align data-current/previous-checked with the real :checked DOM state.
   *   3. Re-sync internal #checkedIndices / #radios.
   *   4. Remove no-animate class after two rAFs.
   */
  #resetPillState() {
    const picker = /** @type {VariantPicker} */ (
      document.querySelector('variant-picker[ref="mainVariantPicker"]') ?? this
    );

    // ── 1. Kill transitions for this paint cycle ──────────────────────────
    picker.classList.add('variant-picker--no-animate');

    // ── 2. Align data-current-checked / data-previous-checked with :checked ─
    // Also reset sibling radios (data-fieldset-index="-1") which live in a fieldset
    // that has no data-fieldset-index on the <fieldset> itself.
    picker.querySelectorAll('input[data-fieldset-index]').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;
      el.dataset.previousChecked = 'false';
      el.dataset.currentChecked = el.checked ? 'true' : 'false';
    });

    // ── 3. Re-sync internal #checkedIndices and #radios ──────────────────
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      Array.from(picker.querySelectorAll('fieldset[data-fieldset-index]'))
    );
    this.#checkedIndices = [];
    this.#radios = [];
    fieldsets.forEach((fieldset) => {
      const radios = /** @type {HTMLInputElement[]} */ (Array.from(fieldset.querySelectorAll('input')));
      this.#radios.push(radios);
      const checkedIndex = radios.findIndex((r) => r.checked);
      this.#checkedIndices.push(checkedIndex !== -1 ? [checkedIndex] : []);
    });

    // ── 4. Re-enable transitions after two paint frames ──────────────────
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        picker.classList.remove('variant-picker--no-animate');
      });
    });
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
